import { ReactNode, useEffect, useState, useCallback } from 'react';

import ResizeObserver from 'lib/resize-observer';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { KeyboardInteraction } from '../../shared/interactive-drag-drop/interactive-drag-drop.constants';
import ActivityBuilderInlineSvg from 'approot/shared/inline-svg/activity-builder/activity-builder-inline-svg';
import IconArrowLeft from 'approot/shared/icons/icon-arrow-left';
import * as Styled from './interactive-response-shared.style';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import { SmallStyled } from 'approot/shared/styles/typography.style';

export const InteractiveResponseViewScaler = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [elementDimensions, setElementDimensions] = useState<{
    containerHeight: number;
    scaleHeight: number;
  }>();
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(
    null
  );
  const [scaleElement, setScaleElement] = useState<HTMLElement | null>(null);

  const containerHeight = containerElement?.offsetHeight || 0;
  const scaleHeight = scaleElement?.offsetHeight || 0;

  const computeSize = useCallback(() => {
    setElementDimensions({
      containerHeight,
      scaleHeight,
    });
  }, [containerHeight, scaleHeight]);

  useEffect(() => {
    if (containerHeight > 0 && scaleHeight > 0) {
      computeSize();
    }
  }, [containerHeight, scaleHeight, computeSize]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(computeSize);

    if (containerElement) {
      resizeObserver.observe(containerElement);
    }

    return () => {
      if (containerElement) {
        resizeObserver.unobserve(containerElement);
      }
    };
  }, [containerElement, computeSize]);

  return (
    <Styled.InteractiveStaticCanvas ref={setContainerElement}>
      <Styled.InteractiveStaticCanvasWrapper
        ref={setScaleElement}
        style={{
          transform:
            elementDimensions &&
            elementDimensions.scaleHeight > elementDimensions.containerHeight
              ? `scale(${
                  elementDimensions.containerHeight /
                  elementDimensions.scaleHeight
                })`
              : 'none',
        }}
      >
        <div>{children}</div>
      </Styled.InteractiveStaticCanvasWrapper>
    </Styled.InteractiveStaticCanvas>
  );
};

export const InteractiveResponseTitle = ({
  title,
  subTitle,
}: {
  title: string | null;
  subTitle: string | null;
}) => {
  if (!title && !subTitle) {
    return null;
  }

  return (
    <Styled.InteractiveResponseHeader>
      <Styled.InteractiveResponseTitle data-selector="interactive-title">
        {title}
      </Styled.InteractiveResponseTitle>
      {subTitle && (
        <Styled.InteractiveResponseSubtitle data-selector="interactive-sub-title">
          {subTitle}
        </Styled.InteractiveResponseSubtitle>
      )}
    </Styled.InteractiveResponseHeader>
  );
};

export const InteractiveResponseScreenReader = ({
  keyboardState,
}: {
  keyboardState?: KeyboardInteraction;
}) => (
  <ScreenReaderOnly aria-live="assertive" aria-atomic="true">
    {keyboardState ? keyboardState.announce : ''}
  </ScreenReaderOnly>
);

export const InteractiveResponseAnswerIcons = ({
  isCorrect,
  animate = true,
}: {
  isCorrect?: boolean;
  animate?: boolean;
}) => {
  return (
    <Styled.InteractiveAnswerIcon
      $isAnswered={isCorrect !== undefined}
      $animate={animate}
    >
      {isCorrect === true ? (
        <ActivityBuilderInlineSvg id="icon_correct" width={40} height={40} />
      ) : isCorrect === false ? (
        <ActivityBuilderInlineSvg id="icon_incorrect" width={40} height={40} />
      ) : null}
    </Styled.InteractiveAnswerIcon>
  );
};

export const InteractiveResponseItemCount = ({
  index,
  total,
}: {
  index: number;
  total: number;
}) => (
  <Styled.InteractiveResponseSlideCount>
    {index + 1} of {total}
  </Styled.InteractiveResponseSlideCount>
);

export const InteractiveResponsePrevious = ({
  disabled,
  onClick,
}: {
  disabled?: boolean;
  onClick: () => void;
}) => (
  <Styled.InteractiveResponseSmallButton
    type="button"
    disabled={disabled}
    aria-label="Go to previous item"
    onClick={onClick}
  >
    <IconArrowLeft width={20} height={20} />
  </Styled.InteractiveResponseSmallButton>
);

export const InteractiveResponseNext = ({
  disabled,
  onClick,
}: {
  disabled?: boolean;
  onClick: () => void;
}) => (
  <Styled.InteractiveResponseSmallButton
    type="button"
    disabled={disabled}
    aria-label="Go to next item"
    onClick={onClick}
  >
    <IconArrowLeft width={20} height={20} rotate={180} />
  </Styled.InteractiveResponseSmallButton>
);

export const MATCH_OPTION = 'match';

export const InteractiveResponseFeedbackSetting = ({
  isCheckAnswerAtEnd,
  onSelect,
}: {
  isCheckAnswerAtEnd: boolean;
  onSelect: (value: 'end' | 'during') => void;
}) => {
  return (
    <SettingsGroup>
      <SettingsLabel>Feedback</SettingsLabel>
      <CheckableSingle
        name="feedback"
        isSmall
        selected={isCheckAnswerAtEnd ? 'end' : 'during'}
        options={[
          {
            label: 'At the End',
            value: 'end',
          },
          {
            label: 'Immediately',
            value: 'during',
          },
        ]}
        onSelect={onSelect}
      />
      {isCheckAnswerAtEnd ? (
        <SmallStyled>
          Check your answers after you have completed the activity.
        </SmallStyled>
      ) : (
        <SmallStyled>Check your answers as you go.</SmallStyled>
      )}
    </SettingsGroup>
  );
};
